import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Heart, Clock, ChevronRight, Bookmark, BookmarkCheck, MapPin, Calendar, Crown, Edit2, Check, X, Users, UserMinus, Scissors, CalendarDays, Play, Award, Star, Pencil, Save, Loader2, ExternalLink, Bell, BellRing, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Game card component for History/Favorites
function GameCard({ game, timeLabel, isSaved, onToggleSave }) {
  const homeScore = game.score?.home || 0;
  const awayScore = game.score?.away || 0;
  const isFinal = game.status === 'final' || game.status === 'completed';
  const isLive = game.status === 'live' || game.status === 'in_progress';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <Link
      to={`/game-live/${game.game_id}`}
      className="block bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-600 transition-colors"
      data-testid={`game-card-${game.game_id}`}
    >
      {/* Status badge */}
      <div className="relative px-4 pt-3 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isLive && (
              <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded uppercase tracking-wider">Live</span>
            )}
            {isFinal && (
              <span className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-[10px] font-bold rounded uppercase tracking-wider">Replay</span>
            )}
            {game.event_name && (
              <span className="text-xs text-zinc-500 truncate max-w-[200px]">{game.event_name}</span>
            )}
          </div>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave(game.game_id); }}
            className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
            data-testid={`save-btn-${game.game_id}`}
          >
            {isSaved ? (
              <BookmarkCheck className="w-4 h-4 text-cyan-400" />
            ) : (
              <Bookmark className="w-4 h-4 text-zinc-500" />
            )}
          </button>
        </div>

        {/* Teams & Scores */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: game.home_team_color || '#3B82F6' }} />
              <span className="text-sm text-white font-medium">{game.home_team_name || 'Home'}</span>
            </div>
            <span className={`text-lg font-bold tabular-nums ${homeScore > awayScore && isFinal ? 'text-white' : 'text-zinc-400'}`}>
              {homeScore}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: game.away_team_color || '#EF4444' }} />
              <span className="text-sm text-white font-medium">{game.away_team_name || 'Away'}</span>
            </div>
            <span className={`text-lg font-bold tabular-nums ${awayScore > homeScore && isFinal ? 'text-white' : 'text-zinc-400'}`}>
              {awayScore}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-800">
          {game.location && (
            <span className="flex items-center gap-1 text-xs text-zinc-500">
              <MapPin className="w-3 h-3" />
              {game.location}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs text-zinc-500 ml-auto">
            <Calendar className="w-3 h-3" />
            {formatDate(game.scheduled_time || timeLabel)}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const { token: authToken, isAuthenticated, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('history');
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [followedTeams, setFollowedTeams] = useState([]);
  const [clips, setClips] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  
  // Athlete profile state
  const [claimedAthlete, setClaimedAthlete] = useState(null);
  const [athleteForm, setAthleteForm] = useState({});
  const [savingAthlete, setSavingAthlete] = useState(false);
  const [loadingAthlete, setLoadingAthlete] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    new_announcements: true,
    new_messages: true,
    game_reminders: true,
    rsvp_updates: true,
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchHeaders = useCallback(() => ({
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  }), [authToken]);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch profile
  useEffect(() => {
    if (!authToken) return;
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/user/profile`, { headers: fetchHeaders() });
        if (res.ok) {
          const data = await res.json();
          setProfile(data);
          setEditName(data.display_name || data.name || '');
          setEditRole(data.role || 'fan');
        } else if (res.status === 401) {
          navigate('/login');
        }
      } catch (e) {
        console.error('Failed to fetch profile:', e);
      }
    };
    fetchProfile();
  }, [authToken, fetchHeaders, navigate]);

  // Fetch saved game IDs
  const fetchSavedIds = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_URL}/api/user/saved-games/ids`, { headers: fetchHeaders() });
      if (res.ok) {
        const ids = await res.json();
        setSavedIds(new Set(ids));
      }
    } catch (e) { console.error(e); }
  }, [authToken, fetchHeaders]);

  // Fetch tab data
  useEffect(() => {
    if (!authToken) return;
    setLoading(true);

    const fetchData = async () => {
      try {
        if (activeTab === 'history') {
          const res = await fetch(`${API_URL}/api/user/watch-history`, { headers: fetchHeaders() });
          if (res.ok) setHistory(await res.json());
        } else if (activeTab === 'favorites') {
          const res = await fetch(`${API_URL}/api/user/saved-games`, { headers: fetchHeaders() });
          if (res.ok) setFavorites(await res.json());
        } else if (activeTab === 'following') {
          const res = await fetch(`${API_URL}/api/user/followed-teams`, { headers: fetchHeaders() });
          if (res.ok) setFollowedTeams(await res.json());
        } else if (activeTab === 'clips') {
          const res = await fetch(`${API_URL}/api/clips/my-clips`, { headers: fetchHeaders() });
          if (res.ok) setClips(await res.json());
        } else if (activeTab === 'watchlist') {
          const res = await fetch(`${API_URL}/api/user/bookmarked-athletes`, { headers: fetchHeaders() });
          if (res.ok) setWatchlist(await res.json());
        } else if (activeTab === 'my-profile') {
          setLoadingAthlete(true);
          const res = await fetch(`${API_URL}/api/user/claimed-athlete`, { headers: fetchHeaders() });
          if (res.ok) {
            const data = await res.json();
            if (data.athlete) {
              setClaimedAthlete(data.athlete);
              setAthleteForm({
                bio: data.athlete.bio || '',
                position: data.athlete.position || '',
                height: data.athlete.height || '',
                date_of_birth: data.athlete.date_of_birth || '',
                grade: data.athlete.grade || '',
                school: data.athlete.school || '',
                number: data.athlete.number || '',
                team: data.athlete.team || '',
                graduation_year: data.athlete.graduation_year || '',
                gpa: data.athlete.gpa || '',
                contact_email: data.athlete.contact_email || '',
                social_links: data.athlete.social_links || {},
              });
            }
          }
          setLoadingAthlete(false);
        } else if (activeTab === 'notifications') {
          const [prefsRes, notifsRes] = await Promise.all([
            fetch(`${API_URL}/api/notifications/preferences`, { headers: fetchHeaders() }),
            fetch(`${API_URL}/api/notifications?limit=30`, { headers: fetchHeaders() }),
          ]);
          if (prefsRes.ok) setNotifPrefs(await prefsRes.json());
          if (notifsRes.ok) {
            const data = await notifsRes.json();
            setNotifications(data.notifications || []);
            setUnreadCount(data.unread_count || 0);
          }
        }
        await fetchSavedIds();
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [activeTab, authToken, fetchHeaders, fetchSavedIds]);

  const toggleSave = async (gameId) => {
    const isSaved = savedIds.has(gameId);
    const method = isSaved ? 'DELETE' : 'POST';
    try {
      await fetch(`${API_URL}/api/user/saved-games/${gameId}`, {
        method,
        headers: fetchHeaders(),
      });
      const newIds = new Set(savedIds);
      if (isSaved) {
        newIds.delete(gameId);
        if (activeTab === 'favorites') {
          setFavorites(prev => prev.filter(g => g.game_id !== gameId));
        }
      } else {
        newIds.add(gameId);
      }
      setSavedIds(newIds);
      toast.success(isSaved ? 'Game removed from favorites' : 'Game saved to favorites');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const unfollowTeam = async (teamName) => {
    try {
      await fetch(`${API_URL}/api/user/followed-teams/${encodeURIComponent(teamName)}`, {
        method: 'DELETE',
        headers: fetchHeaders(),
      });
      setFollowedTeams(prev => prev.filter(t => t.team_name !== teamName));
      toast.success(`Unfollowed ${teamName}`);
    } catch (e) {
      toast.error('Failed to unfollow');
    }
  };


  const saveProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/profile`, {
        method: 'PUT',
        headers: fetchHeaders(),
        body: JSON.stringify({
          display_name: editName,
          role: editRole,
        }),
      });
      if (res.ok) {
        setProfile(prev => ({ ...prev, display_name: editName, name: editName, role: editRole }));
        setEditingProfile(false);
        toast.success('Profile updated');
      }
    } catch (e) {
      toast.error('Failed to update profile');
    }
  };

  const saveAthleteProfile = async () => {
    if (!claimedAthlete) return;
    setSavingAthlete(true);
    try {
      const payload = { ...athleteForm };
      if (payload.number) payload.number = parseInt(payload.number) || 0;
      const res = await fetch(`${API_URL}/api/athletes/${claimedAthlete.id}`, {
        method: 'PUT',
        headers: fetchHeaders(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Athlete profile updated!');
        // Refresh data
        const freshRes = await fetch(`${API_URL}/api/athletes/${claimedAthlete.id}`);
        if (freshRes.ok) setClaimedAthlete(await freshRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Error saving profile'); }
    setSavingAthlete(false);
  };

  const roleLabels = {
    fan: 'Fan',
    athlete: 'Player/Athlete',
    coach: 'Coach',
    parent: 'Parent/Guardian',
    scout: 'Scout',
    creator: 'Creator',
    admin: 'Admin',
    operator: 'Operator',
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const displayName = profile.display_name || profile.name || profile.email?.split('@')[0] || 'User';
  const games = activeTab === 'history' ? history : favorites;

  return (
    <div className="min-h-screen bg-rooam-black text-white" data-testid="account-page">
      {/* Profile Header */}
      <div className="bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0" data-testid="profile-avatar">
              <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              {editingProfile ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-white text-sm focus:border-cyan-500 outline-none"
                    placeholder="Display name"
                    data-testid="edit-name-input"
                  />
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-white text-sm focus:border-cyan-500 outline-none"
                    data-testid="edit-role-select"
                  >
                    {['fan', 'athlete', 'coach', 'parent', 'scout', 'creator'].map(r => (
                      <option key={r} value={r}>{roleLabels[r]}</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={saveProfile} className="p-1.5 bg-cyan-600 hover:bg-cyan-500 rounded-lg" data-testid="save-profile-btn">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingProfile(false)} className="p-1.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg" data-testid="cancel-edit-btn">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="text-xl sm:text-2xl font-bold text-white truncate" data-testid="profile-name">
                    {displayName}
                  </h1>
                  <p className="text-sm text-zinc-400">{roleLabels[profile.role] || profile.role}</p>
                  <button
                    onClick={() => setEditingProfile(true)}
                    className="mt-2 flex items-center gap-1.5 px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit Profile
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Card */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="subscription-card">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-semibold text-white">
              {profile.role === 'admin' || profile.role === 'super_admin' || profile.role === 'operator'
                ? 'Admin Access'
                : profile.subscription 
                  ? profile.subscription.plan_name || 'Active Plan' 
                  : 'Free Account'}
            </span>
            {(profile.role === 'admin' || profile.role === 'super_admin' || profile.role === 'operator') && (
              <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-bold rounded uppercase">
                {profile.role === 'super_admin' ? 'Super Admin' : profile.role}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mb-3">
            {profile.role === 'admin' || profile.role === 'super_admin' || profile.role === 'operator'
              ? 'You have full admin access to all features, games, and platform management.'
              : profile.subscription
                ? profile.subscription.cancel_at_period_end
                  ? `Auto-renew is off — your access ends ${profile.subscription.current_period_end ? new Date(profile.subscription.current_period_end * 1000).toLocaleDateString() : 'at the end of this period'}.`
                  : profile.subscription.status === 'cancelled'
                    ? 'Your subscription has been cancelled. You retain access until the end of your billing period.'
                    : 'You have full access to live games, replays, and premium content. Renews automatically.'
                : 'Subscribe to unlock full access to live games and replays.'}
          </p>

          {/* === Forced Migration Banner: legacy one-time-payment users === */}
          {profile.subscription && profile.subscription.legacy && profile.subscription.status === 'active' && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 mb-3 flex items-start gap-3" data-testid="migration-banner">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-300 mb-1">
                  Your plan is moving to auto-renewal
                </p>
                <p className="text-xs text-zinc-300 mb-2">
                  We've upgraded membership to auto-renew so you never lose access on game day.
                  Set up recurring billing in 30 seconds — we'll honor your current period.
                </p>
                <Link
                  to="/membership"
                  className="inline-block px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold rounded-md"
                  data-testid="migration-banner-cta"
                >
                  Set up auto-renew →
                </Link>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            {(profile.role === 'admin' || profile.role === 'super_admin' || profile.role === 'operator') ? (
              <>
                <Link
                  to="/admin"
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium rounded-lg text-center transition-colors"
                  data-testid="admin-dashboard-btn"
                >
                  Admin Dashboard
                </Link>
                <Link
                  to="/membership"
                  className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg text-center transition-colors"
                  data-testid="view-plans-btn"
                >
                  View Plans
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/membership"
                  className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg text-center transition-colors"
                  data-testid="view-account-btn"
                >
                  View Account
                </Link>
                {!profile.subscription && (
                  <Link
                    to="/membership"
                    className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg text-center transition-colors"
                    data-testid="upgrade-btn"
                  >
                    Upgrade
                  </Link>
                )}
                {profile.subscription && profile.subscription.status === 'active' && !profile.subscription.cancel_at_period_end && (
                  <button
                    onClick={async () => {
                      if (!window.confirm('Cancel auto-renewal? You will keep full access until the end of your current billing period — no surprises.')) return;
                      try {
                        const res = await fetch(`${API_URL}/api/membership/cancel`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${authToken}` },
                        });
                        if (res.ok) {
                          const data = await res.json();
                          toast.success(data.message || 'Auto-renewal stopped. Access continues until end of billing period.');
                          fetchProfile();
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || 'Failed to cancel');
                        }
                      } catch {
                        toast.error('Network error');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold rounded-lg text-center transition-colors border border-red-700"
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Plan
                  </button>
                )}

                {/* Reactivate — only when cancel_at_period_end is set */}
                {profile.subscription && profile.subscription.cancel_at_period_end && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/membership/reactivate`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${authToken}` },
                        });
                        if (res.ok) {
                          toast.success('Auto-renewal restored');
                          fetchProfile();
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || 'Failed to reactivate');
                        }
                      } catch {
                        toast.error('Network error');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg text-center transition-colors flex items-center justify-center gap-1.5"
                    data-testid="reactivate-subscription-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Keep Plan
                  </button>
                )}

                {/* Manage Subscription -> opens Stripe Customer Portal. Only for real recurring users. */}
                {profile.subscription && !profile.subscription.legacy && profile.subscription.stripe_customer_id && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/membership/portal`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${authToken}`,
                          },
                          body: JSON.stringify({ origin_url: window.location.origin }),
                        });
                        if (res.ok) {
                          const data = await res.json();
                          if (data.url) {
                            window.location.href = data.url;
                          } else {
                            toast.error('No portal URL returned');
                          }
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || 'Failed to open billing portal');
                        }
                      } catch {
                        toast.error('Network error');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg text-center transition-colors border border-zinc-700 flex items-center justify-center gap-1.5"
                    data-testid="manage-subscription-btn"
                    title="Update payment method, change plan, view invoices"
                  >
                    <Settings className="w-4 h-4" />
                    Manage
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* History / Favorites / Clips / Events / Following Tabs */}
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex gap-4 sm:gap-6 border-b border-zinc-800 mb-4 overflow-x-auto hide-scrollbar">
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'history'
                ? 'text-white border-cyan-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="history-tab"
          >
            <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            History
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'favorites'
                ? 'text-white border-cyan-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="favorites-tab"
          >
            <Heart className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Favorites
          </button>
          <button
            onClick={() => setActiveTab('clips')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'clips'
                ? 'text-white border-cyan-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="clips-tab"
          >
            <Scissors className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Clips
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'events'
                ? 'text-white border-cyan-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="events-tab"
          >
            <CalendarDays className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Events
          </button>
          <button
            onClick={() => setActiveTab('following')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'following'
                ? 'text-white border-cyan-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="following-tab"
          >
            <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Following
          </button>
          <button
            onClick={() => setActiveTab('watchlist')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'watchlist'
                ? 'text-white border-amber-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="watchlist-tab"
          >
            <Star className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Watchlist
          </button>
          <button
            onClick={() => setActiveTab('my-profile')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'my-profile'
                ? 'text-white border-green-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="my-profile-tab"
          >
            <Award className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            My Profile
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap relative ${
              activeTab === 'notifications'
                ? 'text-white border-cyan-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid="notifications-tab"
          >
            <Bell className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Notifications
            {unreadCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded-full">{unreadCount}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'events' ? (
          /* Events Tab - Link to Events Page */
          <div className="text-center py-12" data-testid="events-tab-content">
            <CalendarDays className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Browse All Events</h3>
            <p className="text-zinc-400 text-sm mb-6 max-w-xs mx-auto">
              Explore live games, tournaments, and upcoming events from teams across the country.
            </p>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg transition-colors"
              data-testid="go-to-events-btn"
            >
              <CalendarDays className="w-5 h-5" />
              Go to Events
            </Link>
          </div>
        ) : activeTab === 'clips' ? (
          /* Clips Tab Content */
          clips.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-clips">
              <Scissors className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No clips created yet</p>
              <p className="text-zinc-600 text-xs mt-1">Create clips from game replays to share your favorite moments</p>
              <Link to="/events" className="inline-block mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors">
                Browse Games
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-8" data-testid="clips-list">
              {clips.map((clip) => (
                <Link
                  key={clip.id || clip.clip_id}
                  to={`/clip/${clip.id || clip.clip_id}`}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-600 transition-colors"
                  data-testid={`clip-card-${clip.id || clip.clip_id}`}
                >
                  <div className="aspect-video bg-zinc-800 relative">
                    {clip.thumbnail_url ? (
                      <img src={clip.thumbnail_url} alt={clip.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-10 h-10 text-zinc-600" />
                      </div>
                    )}
                    {clip.duration && (
                      <span className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded">
                        {clip.duration}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-white truncate">{clip.title || 'Untitled Clip'}</h4>
                    <p className="text-xs text-zinc-500 mt-1">
                      {clip.created_at ? new Date(clip.created_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )
        ) : activeTab === 'following' ? (
          /* Following Tab Content */
          followedTeams.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-following">
              <Users className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">You're not following any teams yet</p>
              <p className="text-zinc-600 text-xs mt-1">Follow teams to stay updated on their games</p>
              <Link to="/events" className="inline-block mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors">
                Browse Events
              </Link>
            </div>
          ) : (
            <div className="space-y-2 pb-8" data-testid="following-list">
              {followedTeams.map((team) => (
                <div
                  key={team.team_name}
                  className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-3 hover:border-zinc-600 transition-colors"
                  data-testid={`followed-team-${team.team_name}`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm"
                    style={{ backgroundColor: team.color }}
                  >
                    {team.team_name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/team/${encodeURIComponent(team.team_name)}`} className="text-sm font-semibold text-white hover:text-cyan-400 transition-colors truncate block">
                      {team.team_name}
                    </Link>
                    <div className="flex items-center gap-3 text-xs text-zinc-500">
                      <span>{team.total_games} game{team.total_games !== 1 ? 's' : ''}</span>
                      {team.upcoming_games > 0 && (
                        <span className="text-cyan-400 font-medium">{team.upcoming_games} upcoming</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => unfollowTeam(team.team_name)}
                    className="p-2 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors shrink-0"
                    data-testid={`unfollow-btn-${team.team_name}`}
                    title="Unfollow"
                  >
                    <UserMinus className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )
        ) : activeTab === 'watchlist' ? (
          /* Watchlist Tab Content - Bookmarked Athletes */
          watchlist.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-watchlist">
              <Star className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Your watchlist is empty</p>
              <p className="text-zinc-600 text-xs mt-1">Bookmark athletes to track them here (Elite feature)</p>
              <Link to="/athletes" className="inline-block mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-lg transition-colors">
                Browse Athletes
              </Link>
            </div>
          ) : (
            <div className="space-y-3 pb-8" data-testid="watchlist-list">
              {watchlist.map((athlete) => (
                <Link
                  key={athlete.id}
                  to={`/athlete/${athlete.id}`}
                  className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-3 hover:border-amber-500/30 transition-colors"
                  data-testid={`watchlist-athlete-${athlete.id}`}
                >
                  <img
                    src={athlete.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`}
                    alt={athlete.name}
                    className="w-12 h-12 rounded-full border-2 border-amber-500/50 object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white truncate">{athlete.name}</span>
                      {athlete.verified && (
                        <Award className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 truncate">{athlete.position} • {athlete.team}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded capitalize">{athlete.sport}</span>
                      {athlete.number > 0 && (
                        <span className="text-[10px] text-zinc-500">#{athlete.number}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
                </Link>
              ))}
            </div>
          )
        ) : activeTab === 'my-profile' ? (
          <div className="pb-8" data-testid="my-profile-content">
            {loadingAthlete ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 text-green-500 animate-spin" />
              </div>
            ) : !claimedAthlete ? (
              <div className="text-center py-12">
                <User className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm font-semibold">No Athlete Profile Claimed</p>
                <p className="text-zinc-600 text-xs mt-1 max-w-xs mx-auto">Find your profile in the Athletes directory and click "Claim Profile" to manage your stats, bio, and recruiting presence.</p>
                <Link to="/athletes" className="inline-block mt-4 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors">
                  Browse Athletes
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Profile header */}
                <div className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <img
                    src={claimedAthlete.avatar ? (claimedAthlete.avatar.startsWith('http') ? claimedAthlete.avatar : `${API_URL}${claimedAthlete.avatar}`) : `https://api.dicebear.com/7.x/avataaars/svg?seed=${claimedAthlete.id}`}
                    alt={claimedAthlete.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-green-500/30"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-bold text-lg truncate">{claimedAthlete.name}</h3>
                      {claimedAthlete.number > 0 && <span className="text-zinc-500 text-sm">#{claimedAthlete.number}</span>}
                    </div>
                    <p className="text-zinc-500 text-sm">{[claimedAthlete.position, claimedAthlete.team].filter(Boolean).join(' · ')}</p>
                  </div>
                  <Link to={`/athlete/${claimedAthlete.id}`} className="shrink-0 text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="view-public-profile-link">
                    View <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>

                {/* Edit form */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
                  <h4 className="text-white text-sm font-semibold flex items-center gap-2">
                    <Pencil className="w-4 h-4 text-green-400" /> Edit Athlete Profile
                  </h4>
                  <div>
                    <Label className="text-zinc-400 text-xs">Bio</Label>
                    <textarea
                      value={athleteForm.bio}
                      onChange={e => setAthleteForm(f => ({...f, bio: e.target.value}))}
                      placeholder="Tell coaches and scouts about yourself..."
                      className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-green-500 mt-1 min-h-[70px] resize-y"
                      maxLength={500}
                      data-testid="account-edit-bio"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Position</Label>
                      <Input value={athleteForm.position} onChange={e => setAthleteForm(f => ({...f, position: e.target.value}))} placeholder="e.g. Point Guard" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-position" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Jersey #</Label>
                      <Input type="number" value={athleteForm.number} onChange={e => setAthleteForm(f => ({...f, number: e.target.value}))} placeholder="#" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-number" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Height</Label>
                      <Input value={athleteForm.height} onChange={e => setAthleteForm(f => ({...f, height: e.target.value}))} placeholder={`e.g. 6'2"`} className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-height" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Date of Birth</Label>
                      <Input type="date" value={athleteForm.date_of_birth ? athleteForm.date_of_birth.slice(0,10) : ''} onChange={e => setAthleteForm(f => ({...f, date_of_birth: e.target.value}))} className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-dob" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Grade / Class</Label>
                      <Input value={athleteForm.grade} onChange={e => setAthleteForm(f => ({...f, grade: e.target.value}))} placeholder="e.g. Junior" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-grade" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Graduation Year</Label>
                      <Input value={athleteForm.graduation_year} onChange={e => setAthleteForm(f => ({...f, graduation_year: e.target.value}))} placeholder="e.g. 2027" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-gradyear" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">School</Label>
                      <Input value={athleteForm.school} onChange={e => setAthleteForm(f => ({...f, school: e.target.value}))} placeholder="School" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-school" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Team</Label>
                      <Input value={athleteForm.team} onChange={e => setAthleteForm(f => ({...f, team: e.target.value}))} placeholder="Team" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-team" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Contact Email</Label>
                      <Input type="email" value={athleteForm.contact_email} onChange={e => setAthleteForm(f => ({...f, contact_email: e.target.value}))} placeholder="your@email.com" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-email" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">GPA</Label>
                      <Input value={athleteForm.gpa} onChange={e => setAthleteForm(f => ({...f, gpa: e.target.value}))} placeholder="e.g. 3.8" className="bg-zinc-950 border-zinc-700 text-white mt-1" data-testid="account-edit-gpa" />
                    </div>
                  </div>

                  {/* Social Links */}
                  <div>
                    <Label className="text-zinc-400 text-xs mb-2 block">Social Links</Label>
                    <div className="space-y-2">
                      <Input value={athleteForm.social_links?.instagram || ''} onChange={e => setAthleteForm(f => ({...f, social_links: {...(f.social_links || {}), instagram: e.target.value}}))} placeholder="Instagram handle" className="bg-zinc-950 border-zinc-700 text-white" data-testid="account-edit-instagram" />
                      <Input value={athleteForm.social_links?.twitter || ''} onChange={e => setAthleteForm(f => ({...f, social_links: {...(f.social_links || {}), twitter: e.target.value}}))} placeholder="X / Twitter handle" className="bg-zinc-950 border-zinc-700 text-white" data-testid="account-edit-twitter" />
                      <Input value={athleteForm.social_links?.hudl || ''} onChange={e => setAthleteForm(f => ({...f, social_links: {...(f.social_links || {}), hudl: e.target.value}}))} placeholder="Hudl profile URL" className="bg-zinc-950 border-zinc-700 text-white" data-testid="account-edit-hudl" />
                    </div>
                  </div>

                  <button
                    onClick={saveAthleteProfile}
                    disabled={savingAthlete}
                    className="w-full py-2.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="save-athlete-profile-btn"
                  >
                    {savingAthlete ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Athlete Profile
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : games.length === 0 ? (
          <div className="text-center py-12" data-testid="empty-state">
            {activeTab === 'history' ? (
              <>
                <Clock className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No watch history yet</p>
                <p className="text-zinc-600 text-xs mt-1">Games you watch will appear here</p>
              </>
            ) : (
              <>
                <Heart className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No saved games yet</p>
                <p className="text-zinc-600 text-xs mt-1">Save games to watch them later</p>
              </>
            )}
            <Link to="/events" className="inline-block mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors">
              Browse Games
            </Link>
          </div>
        ) : (
          <div className="space-y-3 pb-8" data-testid="games-list">
            {games.map((game) => (
              <GameCard
                key={game.game_id}
                game={game}
                timeLabel={activeTab === 'history' ? game.watched_at : game.saved_at}
                isSaved={savedIds.has(game.game_id)}
                onToggleSave={toggleSave}
              />
            ))}
          </div>
        )}
      </div>

      {/* Notifications Tab Content */}
      {activeTab === 'notifications' && (
        <div className="max-w-2xl mx-auto px-4 pb-8 space-y-6" data-testid="notifications-tab-content">
          {/* Notification Preferences */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <BellRing className="w-4 h-4 text-cyan-400" /> Email Notification Preferences
              </h3>
              <p className="text-[10px] text-zinc-500 mt-0.5">Choose which emails you receive</p>
            </div>
            <div className="divide-y divide-zinc-800">
              {[
                { key: 'new_announcements', label: 'New Announcements', desc: 'Get notified when your program posts an announcement' },
                { key: 'new_messages', label: 'New Messages', desc: 'Get notified when someone sends a team message' },
                { key: 'game_reminders', label: 'Game Reminders (24hr)', desc: 'Get a reminder 24 hours before game time' },
                { key: 'rsvp_updates', label: 'RSVP Updates', desc: 'Get notified about RSVP changes to your events' },
              ].map(pref => (
                <div key={pref.key} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <span className="text-sm text-white">{pref.label}</span>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{pref.desc}</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !notifPrefs[pref.key];
                      setNotifPrefs(prev => ({ ...prev, [pref.key]: newVal }));
                      setSavingNotifPrefs(true);
                      try {
                        await fetch(`${API_URL}/api/notifications/preferences`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', ...fetchHeaders() },
                          body: JSON.stringify({ [pref.key]: newVal }),
                        });
                        toast.success(`${pref.label} ${newVal ? 'enabled' : 'disabled'}`);
                      } catch { toast.error('Failed to update'); }
                      setSavingNotifPrefs(false);
                    }}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      notifPrefs[pref.key] ? 'bg-cyan-600' : 'bg-zinc-700'
                    }`}
                    data-testid={`notif-toggle-${pref.key}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                      notifPrefs[pref.key] ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Notifications */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Bell className="w-4 h-4 text-cyan-400" /> Recent Notifications
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded-full">{unreadCount} new</span>
                )}
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={async () => {
                    await fetch(`${API_URL}/api/notifications/mark-all-read`, {
                      method: 'POST', headers: fetchHeaders(),
                    });
                    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                    setUnreadCount(0);
                    toast.success('All marked as read');
                  }}
                  className="text-[11px] text-cyan-400 hover:underline"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="divide-y divide-zinc-800/60 max-h-[50vh] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="text-center py-10">
                  <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-600 text-sm">No notifications yet</p>
                </div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n.id}
                    className={`px-4 py-3 transition-colors ${!n.read ? 'bg-cyan-500/[0.04]' : ''}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.read ? 'bg-cyan-400' : 'bg-zinc-700'}`} />
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium ${!n.read ? 'text-white' : 'text-zinc-400'}`}>{n.title}</span>
                        <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{n.body}</p>
                        <span className="text-[10px] text-zinc-600 mt-1 block">
                          {n.created_at ? new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Link */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid="account-settings">
          <div className="px-4 py-3 border-b border-zinc-800">
            <h3 className="text-sm font-semibold text-white">Account Settings</h3>
          </div>
          <div className="divide-y divide-zinc-800">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-zinc-400">Email</span>
              <span className="text-sm text-white">{profile.email}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-zinc-400">Role</span>
              <span className="text-sm text-white">{roleLabels[profile.role] || profile.role}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-zinc-400">Member Since</span>
              <span className="text-sm text-white">
                {profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}
              </span>
            </div>
            <Link to="/legal#privacy-policy" className="flex items-center justify-between px-4 py-3 hover:bg-zinc-800/50 transition-colors">
              <span className="text-sm text-zinc-400">Privacy Policy</span>
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </Link>
            <Link to="/legal#terms-of-use" className="flex items-center justify-between px-4 py-3 hover:bg-zinc-800/50 transition-colors">
              <span className="text-sm text-zinc-400">Terms of Service</span>
              <ChevronRight className="w-4 h-4 text-zinc-600" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
